import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ArrowLeft, Minus, Plus, ShoppingCart, ShoppingBag, MapPin, ExternalLink, Loader2, Star, MessageCircle, Tag } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;
const PLAN_PILL = {
  OURO: "bg-amber-100 text-amber-900 border-amber-300",
  PRATA: "bg-stone-100 text-stone-700 border-stone-300",
  BRONZE: "bg-orange-100 text-orange-900 border-orange-300",
  GRATIS: "bg-stone-100 text-stone-500 border-stone-200",
};

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [mainImg, setMainImg] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/detail/${id}`)
      .then((r) => {
        setData(r.data);
        setMainImg(r.data?.image_url || (r.data?.images || [])[0] || null);
      })
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Produto não encontrado");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-10 flex items-center gap-2 text-stone-500"><Loader2 className="animate-spin"/> Carregando produto...</div>;
  if (!data) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <h1 className="font-display text-2xl font-bold">Produto não encontrado</h1>
      <Link to="/shop" className="text-blue-900 underline text-sm mt-2 inline-block">← Voltar para o Shop</Link>
    </div>
  );

  const p = data;
  const c = p.company || {};
  const isService = p.kind === "service";
  const canCheckout = p.checkout_enabled && p.price > 0 && p.available;
  const stockNum = p.manage_stock ? Math.max(0, parseInt(p.stock_qty || 0, 10) || 0) : null;
  const maxQty = stockNum != null ? stockNum : 99;
  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(maxQty, q + 1));
  const gallery = [p.image_url, ...(p.images || [])].filter(Boolean);

  const addToCart = (goCheckout) => {
    if (!user) { toast.info("Faça login para comprar"); nav("/login"); return; }
    if (user.role !== "CUSTOMER") { toast.error("Apenas clientes podem comprar"); return; }
    for (let i = 0; i < qty; i++) {
      addItem(c.id, c.name, {
        product_id: p.id, title: p.title, unit_price: p.price,
        image_url: p.image_url, cashback_pct_on_checkout: p.cashback_pct_on_checkout || 0,
        turbo_cashback_pct_on_checkout: p.turbo_active ? (p.turbo_cashback_pct || 0) : 0,
      });
    }
    toast.success(`${p.title} adicionado (${qty}x)`);
    if (goCheckout) nav("/checkout");
  };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
      <Link to="/shop" className="inline-flex items-center gap-1 text-xs sm:text-sm text-stone-500 hover:text-stone-900 mb-3 sm:mb-5" data-testid="pd-back">
        <ArrowLeft size={14}/> Voltar para o Shop
      </Link>

      <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
        {/* Imagens */}
        <div>
          <div className="aspect-square bg-stone-100 rounded-2xl overflow-hidden border border-stone-200 relative">
            {mainImg ? (
              <img src={mainImg} alt={p.title} className="w-full h-full object-cover"/>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-300"><ShoppingBag size={64}/></div>
            )}
            {!p.available && (
              <div className="absolute inset-0 bg-stone-900/65 flex items-center justify-center">
                <span className="bg-white text-stone-900 font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider">Esgotado</span>
              </div>
            )}
            <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
              {p.cashback_pct_on_checkout > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-100/95 text-emerald-900 text-[11px] font-bold backdrop-blur-sm shadow">
                  +{p.cashback_pct_on_checkout}% BemCash
                </span>
              )}
              {p.turbo_active && p.turbo_cashback_pct > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[11px] font-bold shadow">
                  ⚡ Turbo +{p.turbo_cashback_pct}%
                </span>
              )}
            </div>
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="pd-gallery">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setMainImg(g)} className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 shrink-0 ${mainImg === g ? "border-blue-900" : "border-stone-200"}`}>
                  <img src={g} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {c.plan && <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${PLAN_PILL[c.plan]}`}>{c.plan}</span>}
            <span className="text-[11px] text-stone-500 px-1.5 py-0.5 rounded bg-stone-100 font-medium">{isService ? "Serviço" : "Produto"}</span>
            {p.category && <span className="inline-flex items-center gap-1 text-[11px] text-stone-500"><Tag size={10}/> {p.category}</span>}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 leading-tight">{p.title}</h1>
          <Link to={`/company/${c.id}`} className="inline-flex items-center gap-1 text-xs sm:text-sm text-blue-900 hover:underline mt-1.5" data-testid="pd-company-link">
            <span className="font-semibold">{c.name}</span>
            {c.city_slug && <span className="text-stone-400">· <MapPin size={10} className="inline"/> {c.city_slug}</span>}
          </Link>

          <div className="mt-4 sm:mt-6">
            {p.price > 0 ? (
              <div className="flex items-baseline gap-2">
                <span className="font-display text-3xl sm:text-4xl font-bold text-blue-950">{fmt(p.price)}</span>
                {p.manage_stock && stockNum > 0 && (
                  <span className="text-xs text-emerald-700 font-medium">· {stockNum} em estoque</span>
                )}
              </div>
            ) : (
              <span className="text-stone-500 italic">Preço sob consulta</span>
            )}
          </div>

          {p.description && (
            <div className="mt-4 sm:mt-5">
              <h2 className="font-display font-semibold text-stone-900 text-sm sm:text-base mb-1.5">Descrição</h2>
              <p className="text-sm text-stone-700 whitespace-pre-line leading-relaxed">{p.description}</p>
            </div>
          )}

          {/* Quantity + Buy */}
          {canCheckout && (
            <div className="mt-5 sm:mt-7 p-3 sm:p-4 rounded-2xl border border-stone-200 bg-stone-50/50 space-y-3" data-testid="pd-buy-box">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-stone-700">Quantidade:</span>
                <div className="inline-flex items-center border border-stone-300 rounded-xl bg-white overflow-hidden">
                  <button onClick={dec} disabled={qty <= 1} className="px-2.5 py-2 hover:bg-stone-100 disabled:opacity-40" data-testid="pd-qty-dec"><Minus size={14}/></button>
                  <span className="px-3 sm:px-4 font-bold text-stone-900 min-w-[2rem] text-center" data-testid="pd-qty">{qty}</span>
                  <button onClick={inc} disabled={qty >= maxQty} className="px-2.5 py-2 hover:bg-stone-100 disabled:opacity-40" data-testid="pd-qty-inc"><Plus size={14}/></button>
                </div>
                <span className="text-xs text-stone-500 ml-auto font-medium">Total: <strong className="text-blue-950">{fmt(p.price * qty)}</strong></span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => addToCart(false)} variant="outline" className="rounded-xl border-blue-900 text-blue-900 hover:bg-blue-50" data-testid="pd-add-cart">
                  <ShoppingCart size={14} className="mr-1.5"/> Adicionar
                </Button>
                <Button onClick={() => addToCart(true)} className="rounded-xl bg-blue-900 hover:bg-blue-950 text-white" data-testid="pd-buy-now">
                  Comprar agora
                </Button>
              </div>
            </div>
          )}

          {!canCheckout && p.sale_url && (
            <a href={p.sale_url} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-900 hover:bg-blue-950 text-white font-bold text-sm" data-testid="pd-external-link">
              Ver oferta <ExternalLink size={14}/>
            </a>
          )}

          {!canCheckout && !p.sale_url && c.whatsapp && (
            <a href={`https://wa.me/${(c.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Olá! Tenho interesse no ${isService ? "serviço" : "produto"}: ${p.title}`)}`} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm" data-testid="pd-whatsapp">
              <MessageCircle size={14}/> Falar pelo WhatsApp
            </a>
          )}

          {!p.available && (
            <p className="mt-5 text-sm text-red-700 font-medium">Este item está temporariamente fora de estoque.</p>
          )}
        </div>
      </div>
    </div>
  );
}
